import moment from 'moment';
import Cookies from 'js-cookie';
import {
  getErrorMessage,
  filterSlotsByArrivalAndDepartureTime,
  isSplash,
  getLatestEntertainmentBookableDate,
  isLatestBookableDateBeforeDepartureDate,
  filterPastEvents,
  getEntertainmentDates,
  filterSplashByFreeFlowStartDate,
  getSwimmingFreeFlowMessage,
} from '../../utils';
import { getIsDineAroundEvent, getDineAroundTypeByEntertainment } from '../../dineAround/dineAroundHelper';
import {
  REQUEST_AVAILABLE_ENTERTAINMENTS,
  RECEIVE_AVAILABLE_ENTERTAINMENTS,
  SET_AVAILABLE_ENTERTAINMENTS_FAILURE,
  CLEAR_AVAILABLE_ENTERTAINMENTS_ERROR_MESSAGE,
} from '../constants/availableEntertainments';

const getSlots = (timeSlots, date, arrivalDateString, departureDateString, useCustomTime, arrivalTime, latestBookingDateEndTime, departureTime, duration) => {
  if (!timeSlots || timeSlots.length === 0) {
    return null;
  }
  return timeSlots
    .filter(slot => moment(date).date() - moment(slot.Starts).date() === 0
      && filterSlotsByArrivalAndDepartureTime(slot, arrivalDateString, departureDateString, useCustomTime, arrivalTime, latestBookingDateEndTime, departureTime))
    .filter(({ Starts }) => filterPastEvents({ startDate: Starts, endTime: moment(Starts).add(duration, 'minute').format('HH:mm') }))
    .sort((slotA, slotB) => moment(slotA.Starts).diff(moment(slotB.Starts), 'minutes'));
};

const getEntertainments = (
  useCustomTime,
  rawEntertainments,
  arrivalDate,
  departureDate,
  splashEntertainment = false,
  arrivalTime,
  latestBookingDateEndTime,
  departureTime,
  isDaBooking,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
  dineAroundActivity = false,
) => {
  if (!rawEntertainments || rawEntertainments.length === 0 || !arrivalDate || !departureDate) {
    return [];
  }
  const entertainments = getEntertainmentDates(arrivalDate, departureDate);
  rawEntertainments.forEach(({ CategoryId: categoryId, Locations: locations = [], Id: activityId, Name: name, Duration: duration }) => {
    if ((splashEntertainment && isSplash(name)) || (!splashEntertainment && !isSplash(name))) {
      locations.forEach(({ TimeSlots: timeSlots = [], Id: locationId, Name: locationName }) => {
        entertainments.forEach(({ date, events }, index) => {
          const isDineAroundItem = getIsDineAroundEvent(categoryId, isDaBooking, dineAroundBreakfastCategory, dineAroundDinnerCategory);
          if ((dineAroundActivity && isDineAroundItem) || (!dineAroundActivity && !isDineAroundItem)) {
            const slots = getSlots(timeSlots, date, arrivalDate, departureDate, useCustomTime, arrivalTime, latestBookingDateEndTime, departureTime, duration);
            const dineAroundType = getDineAroundTypeByEntertainment(categoryId, dineAroundBreakfastCategory, dineAroundDinnerCategory);
            if (slots && slots.length > 0) {
              events.push({
                code: `${index}-${activityId}-${locationId}`,
                isDineAroundItem,
                dineAroundType,
                date: moment(date).format('YYYY-MM-DD'),
                activityId,
                name,
                location: locationName,
                slots,
                duration,
              });
            }
          }
        });
      });
    }
  });
  return entertainments;
};

const initialState = {
  isFetching: false,
  wasCalled: false,
  entertainments: [],
  splashEntertainments: [],
  dineAroundItems: [],
  errorMessage: '',
  swimmingFreeFlowMessage: '',
};

const availableEntertainments = (state = initialState, action) => {
  switch (action.type) {
    case REQUEST_AVAILABLE_ENTERTAINMENTS: {
      return { ...state, isFetching: true };
    }
    case RECEIVE_AVAILABLE_ENTERTAINMENTS: {
      const {
        entertainments = [],
        arrivalTime,
        latestBookingDateEndTime,
        departureTime,
        latestActivityBookingDate,
        isDaBooking,
        dineAroundBreakfastCategory,
        dineAroundDinnerCategory,
        swimmingFreeFlowStartDate,
      } = action;
      const arrivalDate = Cookies.get('_arrivalDate');
      const departureDate = Cookies.get('_departureDate');
      const latestEntertainmentBookableDate = getLatestEntertainmentBookableDate(departureDate, latestActivityBookingDate);
      const useCustomTime = isLatestBookableDateBeforeDepartureDate(departureDate, latestActivityBookingDate);
      const formattedEntertainments = getEntertainments(
        useCustomTime,
        entertainments,
        arrivalDate,
        latestEntertainmentBookableDate,
        false,
        arrivalTime,
        latestBookingDateEndTime,
        departureTime,
        isDaBooking,
        dineAroundBreakfastCategory,
        dineAroundDinnerCategory,
        false,
      );
      const formattedSplashEntertainments = getEntertainments(
        useCustomTime,
        entertainments,
        arrivalDate,
        latestEntertainmentBookableDate,
        true,
        arrivalTime,
        latestBookingDateEndTime,
        departureTime,
        isDaBooking,
        dineAroundBreakfastCategory,
        dineAroundDinnerCategory,
        false,
      );
      const formattedDineAroundItems = getEntertainments(
        useCustomTime,
        entertainments,
        arrivalDate,
        latestEntertainmentBookableDate,
        false,
        arrivalTime,
        latestBookingDateEndTime,
        departureTime,
        isDaBooking,
        dineAroundBreakfastCategory,
        dineAroundDinnerCategory,
        true,
      );
      const filteredSplash = filterSplashByFreeFlowStartDate(formattedSplashEntertainments, swimmingFreeFlowStartDate);
      return {
        ...state,
        isFetching: false,
        entertainments: formattedEntertainments,
        splashEntertainments: filteredSplash,
        dineAroundItems: formattedDineAroundItems,
        rawEntertainments: entertainments,
        errorMessage: '',
        wasCalled: true,
        swimmingFreeFlowMessage: getSwimmingFreeFlowMessage(formattedSplashEntertainments, filteredSplash),
      };
    }
    case SET_AVAILABLE_ENTERTAINMENTS_FAILURE: {
      const { error } = action;
      const errorMessage = getErrorMessage(error);
      return { ...state, isFetching: false, errorMessage, wasCalled: true };
    }
    case CLEAR_AVAILABLE_ENTERTAINMENTS_ERROR_MESSAGE: {
      return { ...state, errorMessage: '' };
    }
    default:
      return state;
  }
};

export default availableEntertainments;
